import React, { CSSProperties, useMemo, useState } from 'react'
import { PrescImage } from '../../../../types/models/PrescImage'
import { PrescriptionImageBox } from '../../../../components/molecules/PrescriptionImageBox'
import { Box, useTheme } from '@mui/material'
import { ImageArea } from '../../../../types/models/Presc'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { getRotatedBox, NaturalSize } from '../../../../helpers/imageRotation'

/** 処方箋画像の手動回転角。ページ単位の一時状態で、永続化しない */
export type ManualRotation = 0 | 90 | 180 | 270

type Options = {
  /** 操作列（‹ › 送り・↻ 回転・⤢ 原寸ライトボックス）を出す。既定は従来どおり出さない */
  viewerControls?: boolean
}

/**
 * 表示中の画像 1 枚に属する一時状態。URL が変われば（ページ送り・処方箋の切替）初期値に戻る。
 * 同じ URL のまま（1 ページで送りを押す等）なら保たれる
 */
type PageState = {
  url: string
  naturalSize: NaturalSize | null
  manualRotation: ManualRotation
}
const EMPTY_PAGE: Omit<PageState, 'url'> = {
  naturalSize: null,
  manualRotation: 0,
}

export const usePrescriptionImageBox = (
  prescriptionImages: PrescImage[],
  imageArea: ImageArea[],
  options: Options = {}
) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [prescImageShown, setPrescImageShown] = useState(false)
  const [isZoomed, setIsZoomed] = useState(true)
  const [style, setStyle] = useState({} as CSSProperties)
  const [containerAspectRatio, setContainerAspectRatio] = useState(
    'auto' as string
  )
  // 自動傾き補正（isImageRotatable）でいまかかっている角度。手動回転はこれに足す
  const [autoRotation, setAutoRotation] = useState(0)
  const [pageState, setPageState] = useState<PageState | null>(null)
  const theme = useTheme()

  // 処方箋が切り替わってページ数が減ったら末尾に丸める（範囲外だと画像枠ごと消える）
  const currentIndex = Math.min(
    selectedIndex,
    Math.max(prescriptionImages.length - 1, 0)
  )
  const currentUrl = prescriptionImages[currentIndex]?.image_url
  const page =
    pageState && pageState.url === currentUrl ? pageState : EMPTY_PAGE
  const updatePage = (patch: Partial<Omit<PageState, 'url'>>) => {
    if (!currentUrl) return
    setPageState((prev) => ({
      ...(prev?.url === currentUrl ? prev : EMPTY_PAGE),
      ...patch,
      url: currentUrl,
    }))
  }

  const showNextImage = () => {
    const isLast = currentIndex === prescriptionImages.length - 1
    setSelectedIndex(isLast ? 0 : currentIndex + 1)
  }
  const showPrevImage = () => {
    const isFirst = currentIndex === 0
    setSelectedIndex(isFirst ? prescriptionImages.length - 1 : currentIndex - 1)
  }
  const rotateImage = () => {
    updatePage({
      manualRotation: ((page.manualRotation + 90) % 360) as ManualRotation,
    })
  }

  const setImageStyle = (target: HTMLImageElement, isZoomed: boolean) => {
    const currentImageArea = imageArea[currentIndex]
    const isImageRotatable = localStorageWrapper.getItem('isImageRotatable')
    if (!isImageRotatable || !currentImageArea) {
      setStyle({ width: '100%' })
      setAutoRotation(0)
      return
    }

    // original image size
    const aspectRatio = target.naturalHeight / target.naturalWidth
    const prescriptionAreaAspectRatio =
      getLength(
        currentImageArea[1],
        currentImageArea[2],
        target.width,
        target.height
      ) /
      getLength(
        currentImageArea[0],
        currentImageArea[1],
        target.width,
        target.height
      )

    if (isZoomed) {
      setStyle(getZoomedStyle(currentImageArea, aspectRatio))
      setContainerAspectRatio(`1 / ${prescriptionAreaAspectRatio}`)
      setAutoRotation(calculateRotationInDeg(currentImageArea))
    } else {
      setStyle({ width: '100%' })
      setContainerAspectRatio(`1 / ${aspectRatio}`)
      setAutoRotation(0)
    }
  }

  // 表示中の角度。手動回転中は領域ズームを使わず、この角度で回した全体が容器に収まるスタイルに差し替える
  const rotation = autoRotation + page.manualRotation
  const manualStyle = useMemo(
    () =>
      page.manualRotation !== 0 && page.naturalSize
        ? getRotatedImageStyle(rotation, page.naturalSize)
        : null,
    [page.manualRotation, page.naturalSize, rotation]
  )

  const PrescImageBox = currentUrl && (
    <PrescriptionImageBox
      imageIndex={currentIndex}
      imagesLength={prescriptionImages.length}
      imageUrl={currentUrl}
      onPageIndicatorClick={showNextImage}
      viewerControls={
        options.viewerControls
          ? {
              rotation,
              naturalSize: page.naturalSize,
              onPrevClick: showPrevImage,
              onRotateClick: rotateImage,
            }
          : undefined
      }
      onLoad={(event) => {
        const target = event.target as HTMLImageElement
        updatePage({
          naturalSize: { w: target.naturalWidth, h: target.naturalHeight },
        })
        setImageStyle(target, true)
        setIsZoomed(true)
      }}
      onImageClick={(event) => {
        if (page.manualRotation !== 0) return
        const target = event.target as HTMLImageElement
        setImageStyle(target, !isZoomed)
        setIsZoomed(!isZoomed)
      }}
      imageStyle={manualStyle?.imageStyle ?? style}
      styleForSmallScreen={
        prescImageShown ? { width: '100%' } : { display: 'none' }
      }
      containerAspectRatio={
        manualStyle?.containerAspectRatio ?? containerAspectRatio
      }
    />
  )

  const PrescImageToggleButton = (
    <Box
      sx={{
        [theme.breakpoints.up('md')]: {
          display: 'none',
        },
      }}
      style={
        prescImageShown ? { right: 0, position: 'fixed', height: '100%' } : {}
      }
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={16}
      bgcolor={'secondary.dark'}
      color="#fff"
      onClick={() =>
        prescImageShown ? setPrescImageShown(false) : setPrescImageShown(true)
      }
    >
      {prescImageShown ? '処方箋を閉じる' : '処方箋を開く'}
    </Box>
  )

  return { prescImageShown, PrescImageBox, PrescImageToggleButton }
}

/** 自動傾き補正の核。この hook と latest の画像ペインが同じ実装を共有する。 */
export const getLength = (
  point1: { x座標: number; y座標: number },
  point2: { x座標: number; y座標: number },
  width: number,
  height: number
) =>
  Math.sqrt(
    Math.pow((point1.x座標 - point2.x座標) * width, 2) +
      Math.pow((point1.y座標 - point2.y座標) * height, 2)
  )

export const calculateRotationInDeg = (target: ImageArea): number => {
  return (
    -Math.atan2(
      target[1].y座標 - target[0].y座標,
      target[1].x座標 - target[0].x座標
    ) *
    (180 / Math.PI)
  )
}

const calculateZoomRatio = (target: ImageArea, aspectRatio: number): number => {
  const x = target[1].x座標 - target[0].x座標
  const y = (target[1].y座標 - target[0].y座標) * aspectRatio
  return 1 / Math.sqrt(x * x + y * y)
}

const intoPercent = (value: number): string => {
  return `${value * 100}%`
}

export const getZoomedStyle = (
  target: ImageArea,
  aspectRatio: number
): CSSProperties => {
  const rotation = calculateRotationInDeg(target)
  const zoomRatio = calculateZoomRatio(target, aspectRatio)
  const origin = target[0]

  return {
    width: '100%',
    transform:
      `translate(${intoPercent(-origin.x座標)}, ` +
      `${intoPercent(-origin.y座標)}) ` +
      `rotate(${rotation}deg) ` +
      `scale(${zoomRatio})`,
    transformOrigin:
      `${intoPercent(origin.x座標)} ` + `${intoPercent(origin.y座標)}`,
  }
}

/**
 * 手動回転時のスタイル。容器（width:100%・aspect-ratio・overflow:hidden）は動かさず、
 * 回転後の外接矩形 (bw × bh) が容器にちょうど収まるように容器比と scale を決める。
 *
 * 幅 W で置いた画像を回すと外接矩形の幅は W·bw/w なので、幅を W に合わせる scale は w/bw
 * （90° なら w/h、180° なら 1）。
 * 容器と画像の layout 上の中心はずれるので、画像は absolute で容器中央に置いてから回す。
 */
export const getRotatedImageStyle = (
  rotation: number,
  size: NaturalSize
): { imageStyle: CSSProperties; containerAspectRatio: string } => {
  const { bw, bh } = getRotatedBox(size, rotation)
  return {
    containerAspectRatio: `${bw} / ${bh}`,
    imageStyle: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '100%',
      transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${
        size.w / bw
      })`,
    },
  }
}
